import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

import { API_URL } from '@/config'
import { addAttributes } from '@/lib/sessions'

export const schedule = {

  namespaced: true,

  state: {
    days: [],
    hours: {},
    rooms: [],
    sessions: [],
    infos: [],
    size: {
      label: 40,
      horizontal: {
        top: 43,
        left: 60,
        room: 'M',
        hour: 'M',
      },
      vertical: {
        top: 28,
        left: 220,
        room: 'M',
        hour: 'M',
      },
    },
    sizes: {
      horizontal: {
        room: [],
        hour: [
          { size: .5, label: 'S' },
          { size: 1, label: 'M' },
          { size: 2, label: 'L' }
        ],
      },
      vertical: {
        room: [],
        hour: [
          { size: 1, label: 'S' },
          { size: 2, label: 'M' },
          { size: 4, label: 'L' }
        ],
      },
    },
    keys: {
      horizontal: {
        room: {
          increase: 39,
          decrease: 37,
        },
        hour: {
          increase: 38,
          decrease: 40,
        },
      },
      vertical: {
        room: {
          increase: 40,
          decrease: 38,
        },
        hour: {
          increase: 37,
          decrease: 39,
        },
      },
    },
    add: {
      day_id: null,
      room_id: null,
      start_time: '',
      end_time: '',
    },
    undo: [],
  },

  getters: {
    days: state => state.days,
    hours: state => state.hours,
    rooms: state => state.rooms,
    sessions: state => state.sessions,
    infos: state => state.infos,
    size: state => state.size,
    sizes: state => state.sizes,
    keys: state => state.keys,
    add: state => state.add,
    undo: state => state.undo,
  },

  mutations: {
    setDays(state, days) { state.days = days },
    setHours(state, hours) { state.hours = hours }, 
    setRooms(state, rooms) { state.rooms = rooms },
    setSessions(state, sessions) {
      state.sessions = addAttributes(sessions);
    },
    setInfos(state, infos) {
      state.infos = addAttributes(infos);
    },
    setHorizontalRoomSize(state, room) {
      state.size.horizontal.room = room;
    },
    setHorizontalHourSize(state, hour) {
      state.size.horizontal.hour = hour;
    },
    setVerticalRoomSize(state, room) {
      state.size.vertical.room = room;
    },
    setVerticalHourSize(state, hour) {
      state.size.vertical.hour = hour;
    },
    setAdd(state, add) {
      state.add.day_id = add.day_id;
      state.add.room_id = add.room_id;
      state.add.start_time = add.start_time;
      state.add.end_time = add.end_time;
    },
    unshiftUndo(state, undo) {
      state.undo.unshift(undo);
    },
    popUndo(state) {
      state.undo.pop();
    },
    clearSchedule(state) {
      state.days = [];
      state.hours = [];
      state.rooms = [];
      state.infos = [];
      state.sessions = [];
    },
  },

  actions: {

    getDays: () => Vue.axios.get(API_URL + '/days', { params: {
      sorts: [
        { 'column': 'days.order', 'order': 'asc'}
      ],
    }}),

    getHours: () => Vue.axios.get(API_URL + '/hours'),

    getRooms: () => Vue.axios.get(API_URL + '/rooms', { params: {
      filters: [
        { column: 'publish', 'value': true, status: true },
      ],
      sorts: [
        { 'column': 'rooms.order', 'order': 'asc'}
      ],
    }}),

    getInfos: () => Vue.axios.get(API_URL + '/sessions', { params: {
      filters: [
        { column: 'day_id', not: true, value: null, status: true },
        { column: 'start_time', not: true, value: null, status: true },
        { column: 'end_time', not: true, value: null, status: true },
        { column: 'room_id', not: true, value: null, status: true },
        { column: 'publish', relation: 'room', value: true, status: true },
        { column: 'publish', value: true, status: true },
        { column: 'display_type_id', value: 2, status: true },
      ],
      sorts: [
        { column: 'day_id:days:order', order: 'asc'},
        { column: 'room_id:rooms:order', order: 'asc'},
        { column: 'start_time', order: 'asc'},
        { column: 'id', order: 'asc'},
      ],
      limitless: true 
    }}),

    getSessions: () => Vue.axios.get(API_URL + '/sessions', { params: {
      filters: [
        { column: 'day_id', not: true, value: null, status: true },
        { column: 'start_time', not: true, value: null, status: true },
        { column: 'end_time', not: true, value: null, status: true },
        { column: 'room_id', not: true, value: null, status: true },
        { column: 'publish', relation: 'room', value: true, status: true },
        { column: 'publish', value: true, status: true },
        { column: 'display_type_id', value: '1,3', status: true },
        { column: 'collection', custom: true, value: 'is_not_child', status: true },
      ],
      sorts: [
        { column: 'day_id:days:order', order: 'asc'},
        { column: 'room_id:rooms:order', order: 'asc'},
        { column: 'start_time', order: 'asc'},
        { column: 'id', order: 'asc'},
      ],
      limitless: true 
    }}),

    async getSchedule({ commit, dispatch }) {
      const [days,hours,rooms,infos,sessions] = await Promise.all([
        dispatch('getDays'),
        dispatch('getHours'),
        dispatch('getRooms'),
        dispatch('getInfos'),
        dispatch('getSessions'),
      ]);
      commit('setProgress', false, { root: true });
      commit('setDays', days.data.items);
      commit('setHours', hours.data);
      commit('setRooms', rooms.data.items);
      commit('setInfos', infos.data.items);
      commit('setSessions', sessions.data.items.filter(s => rooms.data.items.map(r => r.id).includes(s.room_id)));
    },

    getUndo({ state }) {
      return state.undo.shift();
    },

    updateSession: async ({ commit, dispatch }, session) => {
      await Vue.axios.put(API_URL + '/sessions/' + session.id, {
        day_id: session.day_id,
        room_id: session.room_id,
        start_time: session.start_time,
        end_time: session.end_time,
      });
      let sessions = await dispatch('getSessions');
      commit('setSessions', sessions.data.items);
      commit('setProgress', false, { root: true });
    },

    cloneSession: async ({ commit, dispatch }, session) => {
      await Vue.axios.post(API_URL + '/sessions/' + session.id + '/scheduleclone');
      let sessions = await dispatch('getSessions');
      commit('setSessions', sessions.data.items);
      commit('setProgress', false, { root: true });
    },

    unpublishSession: async ({ commit, dispatch }, session) => {
      await Vue.axios.put(API_URL + '/sessions/' + session.id, {
        publish: false,
        publish_app: false,
      });
      let sessions = await dispatch('getSessions');
      commit('setSessions', sessions.data.items);
      commit('setProgress', false, { root: true });
    },

    setUserScheduleSettings: (id, settings) => {
      Vue.axios.put(API_URL + '/users/' + id, { settings: settings });
    },

  }
}
